import { getNavigation, getPartners, NavigationProps } from '@amf/shared/utils/navigation';
import { useTranslate } from '@tolgee/react';
import SeasonHeader from '@amf/shared/components/season/SeasonHeader';
import { useEffect, useState } from 'react';
import { SeasonFragment, SeasonLeagueFragment } from '@amf/shared/types/graphql';
import { GetServerSideProps } from 'next';
import { sdk } from '@amf/shared/lib/graphQlClient';
import { getLanguageLocale } from '@amf/shared/utils/languages';
import SeasonPlayerStatsTable from '@amf/shared/components/stats/SeasonPlayerStatsTable';
import Menu from '@amf/shared/components/menu/Menu';

import Meta from 'components/Meta';
import Layout from 'components/layout/Layout';
import { AppLinks } from 'utils/links';

interface StatsPageProps extends NavigationProps {
  seasons: SeasonFragment[];
}

function StatsPage({ navigation, partners, seasons }: StatsPageProps) {
  const { t } = useTranslate();
  const [season, setSeason] = useState<SeasonFragment>(seasons[0]);
  const [league, setLeague] = useState<SeasonLeagueFragment | undefined>(season.leagues[0]);

  useEffect(() => setLeague(season.leagues[0]), [season]);

  return (
    <Layout navigation={navigation} partners={partners}>
      <Meta title={t('stats.title')} />
      <SeasonHeader
        title={t('stats.title')}
        seasons={seasons}
        selected={season}
        onSeasonChange={season => setSeason(season)}
      />
      {league && (
        <>
          <Menu<SeasonLeagueFragment>
            scrollable
            onChange={({ value }) => setLeague(value)}
            selected={{ id: league.id, title: league.name, value: league }}
            items={season.leagues.map(league => ({
              id: league.id,
              title: league.name,
              value: league,
            }))}
          />
          <SeasonPlayerStatsTable
            season={season}
            league={league}
            prefixes={{ player: AppLinks.player, team: AppLinks.team }}
            disableAssists
            disablePoints
          />
        </>
      )}
    </Layout>
  );
}

export default StatsPage;

export const getServerSideProps: GetServerSideProps<StatsPageProps> = async ({ locale, res }) => {
  res.setHeader('Cache-Control', 'max-age=0, s-maxage=10');
  const { seasons } = await sdk.SeasonList({ union: process.env.NEXT_PUBLIC_UNION });
  if (seasons.length === 0) return { notFound: true };

  return {
    props: {
      seasons,
      navigation: await getNavigation(getLanguageLocale(locale!)),
      partners: await getPartners(),
    },
  };
};
